import { useEffect, useState } from 'react'

import { COMPACT_MEDIA_QUERY, MOBILE_MEDIA_QUERY } from '../constants/responsive'

const matchesNow = (query: string): boolean => (
  typeof window.matchMedia === 'function' && window.matchMedia(query).matches
)

/**
 * `true` khi media query đang khớp, tự cập nhật khi đổi độ rộng (xoay máy, kéo cửa sổ).
 *
 * Giá trị đầu đọc ĐỒNG BỘ ngay lúc render đầu tiên — KHÔNG dùng `Grid.useBreakpoint()` của antd: hook đó trả `{}` ở
 * lượt đầu rồi mới đăng ký, nên màn mobile vẽ bảng desktop một khung hình rồi mới đổi sang thẻ (ô nhập mount hai lần).
 *
 * Không có `matchMedia` (jsdom của CRA) ⇒ `false` = bố cục desktop, đúng như các test cũ đang giả định.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => matchesNow(query))

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const list = window.matchMedia(query)
    const update = (): void => setMatches(list.matches)
    // Độ rộng có thể đã đổi giữa lượt render đầu và lúc effect chạy.
    update()
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', update)
      return () => list.removeEventListener('change', update)
    }
    // Safari < 14 chỉ có API cũ.
    list.addListener(update)
    return () => list.removeListener(update)
  }, [query])

  return matches
}

/** Điện thoại — `MOBILE_MEDIA_QUERY` (≤767px). */
export const useIsMobile = (): boolean => useMediaQuery(MOBILE_MEDIA_QUERY)

/** Điện thoại HOẶC máy tính bảng — `COMPACT_MEDIA_QUERY` (≤1199px). */
export const useIsCompact = (): boolean => useMediaQuery(COMPACT_MEDIA_QUERY)
